import { type FC, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { dibsLazyClient } from 'dibs-lazy/src/dibsLazyClient';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { ClientSuspense } from 'dibs-elements/src/ClientSuspense';

// styles
import styles from './PdpSharedSellerDetailsBio.scss';

// types
import { type PdpSharedSellerDetailsBio_seller$key as Seller } from './__generated__/PdpSharedSellerDetailsBio_seller.graphql';

// lazy components
const PdpSharedSellerDetailsBioModal = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedSellerDetailsBioModal" */ './PdpSharedSellerDetailsBioModal'
        )
);

const sellerFragment = graphql`
    fragment PdpSharedSellerDetailsBio_seller on Seller {
        serviceId
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        liveStorefrontProfile {
            aboutUsUnbranded
        }
        ...PdpSharedSellerDetailsBioModal_seller
    }
`;

export const PdpSharedSellerDetailsBio: FC<{
    seller: Seller;
}> = ({ seller: sellerRef }) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const { isRewardsTier2, liveStorefrontProfile } = seller || {};
    const [isBioModalOpen, setIsBioModalOpen] = useState<boolean>(false);

    const openBioModal = useCallback(() => setIsBioModalOpen(true), []);
    const closeBioModal = useCallback(() => setIsBioModalOpen(false), []);

    const { aboutUsUnbranded: aboutUs } = liveStorefrontProfile || {};

    if (!aboutUs || !isRewardsTier2) {
        return null;
    }

    return (
        <div className={styles.container}>
            <span className={styles.textContainer}>
                <p className={styles.text} dangerouslySetInnerHTML={{ __html: aboutUs }} />
                <Link className={styles.readMore} onClick={openBioModal}>
                    <FormattedMessage
                        id="PdpSharedSellerDetailsBio.readMore"
                        defaultMessage="Read More"
                    />
                </Link>
                <ClientSuspense fallback={null}>
                    <PdpSharedSellerDetailsBioModal
                        seller={seller}
                        isOpen={isBioModalOpen}
                        onClose={closeBioModal}
                    />
                </ClientSuspense>
            </span>
        </div>
    );
};
